/**
 * Rider Selector Component
 *
 * Collapsible component for selecting which riders are included in an event.
 * Groups riders into Coaches and Students, supports search, group toggles,
 * and individual checkboxes.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 7.1, 7.2, 7.3, 7.4, 7.5
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { Rider, restClient } from '../../../api/rest-client';
import { LoadingSpinner } from '../../common/LoadingSpinner';

// --- Pure logic functions (exported for testing) ---

export interface GroupedRiders {
  coaches: Rider[];
  students: Rider[];
}

/**
 * Classify a rider as Coach or Student.
 * A rider is a Coach iff rider.type === 'Coach'; everything else is Student
 * (including 'Rider', 'Student', or undefined).
 */
export function isCoach(rider: Rider): boolean {
  return rider.type === 'Coach';
}

/**
 * Group riders into Coaches and Students.
 * Requirement 1.2: Coaches vs everything else (legacy 'Rider' type treated as Student).
 */
export function groupRiders(riders: Rider[]): GroupedRiders {
  const coaches: Rider[] = [];
  const students: Rider[] = [];
  for (const rider of riders) {
    if (isCoach(rider)) {
      coaches.push(rider);
    } else {
      students.push(rider);
    }
  }
  return { coaches, students };
}

/**
 * Filter riders by search string (case-insensitive substring match on name).
 * Requirement 7.1, 7.2
 */
export function filterBySearch(riders: Rider[], search: string): Rider[] {
  if (!search) return riders;
  const lower = search.toLowerCase();
  return riders.filter((r) => r.name.toLowerCase().includes(lower));
}

/**
 * Sort riders alphabetically by name (ascending).
 * Requirement 7.3
 */
export function sortRiders(riders: Rider[]): Rider[] {
  return [...riders].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Apply a group toggle: select or deselect all riders in a group,
 * without affecting riders in the other group.
 * Requirement 1.5, 1.6
 */
export function applyGroupToggle(
  currentSelectedIds: Set<string>,
  groupRiderIds: string[],
  select: boolean
): Set<string> {
  const result = new Set(currentSelectedIds);
  for (const id of groupRiderIds) {
    if (select) {
      result.add(id);
    } else {
      result.delete(id);
    }
  }
  return result;
}

// --- Component ---

export interface RiderSelectorProps {
  /** Pre-selected rider IDs (edit mode). Undefined = all selected. */
  selectedRiderIds?: string[];
  /** Current season string (e.g., "2025") */
  season: string;
  /** Callback when selection changes. undefined = all selected. */
  onSelectionChange: (selectedIds: string[] | undefined) => void;
}

export function RiderSelector({ selectedRiderIds, season, onSelectionChange }: RiderSelectorProps) {
  const [riders, setRiders] = useState<Rider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  // Fetch riders
  const fetchRiders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetched = await restClient.getRiders(season);
      setRiders(fetched);

      // Initialize selection only on first load
      if (!initialized) {
        if (selectedRiderIds) {
          setSelectedIds(new Set(selectedRiderIds));
        } else {
          // All selected by default
          setSelectedIds(new Set(fetched.map((r) => r.rider_id)));
        }
        setInitialized(true);
      }
    } catch {
      setError('Failed to load riders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [season, selectedRiderIds, initialized]);

  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  // Grouped, sorted, and filtered riders
  const grouped = useMemo(() => groupRiders(riders), [riders]);
  const filteredCoaches = useMemo(
    () => sortRiders(filterBySearch(grouped.coaches, search)),
    [grouped.coaches, search]
  );
  const filteredStudents = useMemo(
    () => sortRiders(filterBySearch(grouped.students, search)),
    [grouped.students, search]
  );

  const totalCount = riders.length;
  const selectedCount = selectedIds.size;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  // Notify parent of selection changes
  const notifyChange = useCallback(
    (newIds: Set<string>) => {
      setSelectedIds(newIds);
      if (newIds.size === riders.length && riders.length > 0) {
        onSelectionChange(undefined);
      } else {
        onSelectionChange(Array.from(newIds));
      }
    },
    [riders, onSelectionChange]
  );

  // Group toggle handlers
  const allCoachIds = useMemo(() => grouped.coaches.map((r) => r.rider_id), [grouped.coaches]);
  const allStudentIds = useMemo(() => grouped.students.map((r) => r.rider_id), [grouped.students]);

  const allCoachesSelected = allCoachIds.length > 0 && allCoachIds.every((id) => selectedIds.has(id));
  const allStudentsSelected = allStudentIds.length > 0 && allStudentIds.every((id) => selectedIds.has(id));

  const handleCoachToggle = () => {
    const newIds = applyGroupToggle(selectedIds, allCoachIds, !allCoachesSelected);
    notifyChange(newIds);
  };

  const handleStudentToggle = () => {
    const newIds = applyGroupToggle(selectedIds, allStudentIds, !allStudentsSelected);
    notifyChange(newIds);
  };

  const handleIndividualToggle = (riderId: string) => {
    const newIds = new Set(selectedIds);
    if (newIds.has(riderId)) {
      newIds.delete(riderId);
    } else {
      newIds.add(riderId);
    }
    notifyChange(newIds);
  };

  // Loading state
  if (loading) {
    return (
      <div className="border rounded-lg p-4 flex items-center gap-2 text-gray-500">
        <LoadingSpinner size="sm" />
        <span>Loading riders...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="border border-red-300 rounded-lg p-4 bg-red-50">
        <p className="text-red-600 text-sm">{error}</p>
        <button
          type="button"
          onClick={fetchRiders}
          className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      {/* Collapsed header / toggle */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 rounded-lg"
        aria-expanded={expanded}
      >
        <span className="font-medium text-gray-700">
          Riders: {selectedCount}/{totalCount} riders selected
          {allSelected && ' (all)'}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Search input */}
          <input
            type="text"
            placeholder="Search riders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Search riders"
          />

          {/* Coaches group */}
          {filteredCoaches.length > 0 && (
            <div>
              <label className="flex items-center gap-2 font-medium text-sm text-gray-600 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allCoachesSelected}
                  onChange={handleCoachToggle}
                  className="rounded"
                />
                Select All Coaches ({grouped.coaches.filter((r) => selectedIds.has(r.rider_id)).length}/{grouped.coaches.length})
              </label>
              <div className="ml-6 space-y-1">
                {filteredCoaches.map((rider) => (
                  <label key={rider.rider_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(rider.rider_id)}
                      onChange={() => handleIndividualToggle(rider.rider_id)}
                      className="rounded"
                    />
                    {rider.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Students group */}
          {filteredStudents.length > 0 && (
            <div>
              <label className="flex items-center gap-2 font-medium text-sm text-gray-600 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allStudentsSelected}
                  onChange={handleStudentToggle}
                  className="rounded"
                />
                Select All Students ({grouped.students.filter((r) => selectedIds.has(r.rider_id)).length}/{grouped.students.length})
              </label>
              <div className="ml-6 space-y-1">
                {filteredStudents.map((rider) => (
                  <label key={rider.rider_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(rider.rider_id)}
                      onChange={() => handleIndividualToggle(rider.rider_id)}
                      className="rounded"
                    />
                    {rider.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {filteredCoaches.length === 0 && filteredStudents.length === 0 && search && (
            <p className="text-sm text-gray-500 italic">No riders match "{search}"</p>
          )}
        </div>
      )}
    </div>
  );
}

export default RiderSelector;
